"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import Link from "next/link";
import { PlayCircle, ChevronRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  sportsbookIdToDisplay,
  type SportsbookId,
} from "@/lib/sportsbook-registry";
import { phCapture } from "@/utils/analytics";
import { saveOnboardingPreferences } from "@/lib/actions/user-preferences";
import { SportsbookSelectionField } from "@/components/sportsbook-preferences/sportsbook-selection-field";
import { useSportsbookPreferences } from "@/contexts/sportsbook-preferences-context";

const DEFAULT_BOOKS: SportsbookId[] = ["fanduel", "draftkings"];

const SPORTS_OPTIONS = [
  { id: "NFL", label: "NFL" },
  { id: "NBA", label: "NBA" },
  { id: "MLB", label: "MLB" },
  { id: "NCAAF", label: "NCAAF" },
  { id: "NCAAB", label: "NCAAB" },
  { id: "NCAAW", label: "NCAAW" },
  { id: "Soccer", label: "Soccer" },
  { id: "Tennis", label: "Tennis" },
  { id: "Golf", label: "Golf" },
  { id: "MMA", label: "MMA" },
] as const;

const DEFAULT_SPORTS = ["NBA"];

interface WelcomeModalProps {
  open: boolean;
  onStartTour: () => void;
  onSkip: () => void;
  onSurveyComplete?: (prefs: {
    sportsbooks: string[];
    sports: string[];
  }) => void;
}

type SurveyStep = "books" | "sports" | "done";

export function WelcomeModal({
  open,
  onStartTour,
  onSkip,
  onSurveyComplete,
}: WelcomeModalProps) {
  const [step, setStep] = useState<SurveyStep>("books");
  const [selectedBooks, setSelectedBooks] = useState<Set<string>>(
    () => new Set(DEFAULT_BOOKS),
  );
  const [defaultBookId, setDefaultBookId] = useState<SportsbookId>(DEFAULT_BOOKS[0]);
  const [selectedSports, setSelectedSports] = useState<Set<string>>(
    () => new Set(DEFAULT_SPORTS),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { actions: sportsbookActions, meta: sportsbookMeta } = useSportsbookPreferences();

  const surveyStartedRef = useRef(false);
  useEffect(() => {
    if (open && !surveyStartedRef.current) {
      surveyStartedRef.current = true;
      phCapture("onboarding_survey_started");
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStep("books");
      setSelectedBooks(new Set(DEFAULT_BOOKS));
      setDefaultBookId(DEFAULT_BOOKS[0]);
      setSelectedSports(new Set(DEFAULT_SPORTS));
      setSaving(false);
      setSaveError(null);
      surveyStartedRef.current = false;
    }
  }, [open]);

  const toggleSport = useCallback((sportId: string) => {
    setSelectedSports((prev) => {
      const next = new Set(prev);
      if (next.has(sportId)) {
        next.delete(sportId);
      } else {
        next.add(sportId);
      }
      return next;
    });
  }, []);

  const handleBooksNext = useCallback(() => {
    phCapture("onboarding_survey_step_completed", {
      step: 1,
      books_selected: Array.from(selectedBooks),
    });
    setStep("sports");
  }, [selectedBooks]);

  const handleSportsComplete = useCallback(async () => {
    const books = Array.from(selectedBooks);
    const sports = Array.from(selectedSports);

    setSaving(true);
    setSaveError(null);
    try {
      const sportsResult = await saveOnboardingPreferences({ sports });
      if (!sportsResult.success) {
        setSaveError(
          sportsResult.error ?? "Could not save sport preferences. Try again.",
        );
        return;
      }

      const profileResult = await sportsbookActions.saveProfile({
        mode: "custom",
        bookIds: books,
        defaultBookId,
      });
      if (!profileResult.success) {
        setSaveError(
          profileResult.error ?? "Could not save sportsbook defaults. Try again.",
        );
        return;
      }

      phCapture("onboarding_survey_step_completed", {
        step: 2,
        sports_selected: sports,
      });
      phCapture("onboarding_survey_completed", {
        books_selected: books,
        sports_selected: sports,
      });
      onSurveyComplete?.({ sportsbooks: books, sports });
      setStep("done");
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "Could not save your preferences. Try again.",
      );
    } finally {
      setSaving(false);
    }
  }, [defaultBookId, selectedBooks, selectedSports, onSurveyComplete, sportsbookActions]);

  const handleSkip = useCallback(() => {
    if (saving) return;
    phCapture("onboarding_survey_skipped", { skipped_at_step: step });
    onSkip();
  }, [saving, step, onSkip]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen || saving) return;
      if (step === "done") {
        onSkip();
        return;
      }
      handleSkip();
    },
    [handleSkip, onSkip, saving, step],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-busy={saving}
        onEscapeKeyDown={(event) => {
          if (saving) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (saving) event.preventDefault();
        }}
        className={cn(
          "w-[calc(100vw-1rem)]",
          step === "books" ? "max-w-3xl" : "sm:max-w-lg",
        )}
        contentClassName={cn(
          step === "books" &&
            "h-[min(720px,calc(100dvh-1rem))] min-h-0 grid-rows-[auto_auto_minmax(0,1fr)] sm:h-[min(720px,calc(100dvh-3rem))]",
        )}
      >
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle className="text-xl">
            {step === "done"
              ? "You're all set!"
              : "Welcome to Upside!"}
          </DialogTitle>
          <DialogDescription className="text-base mt-2">
            {step === "books" &&
              "Save the books you use. In tools that support your saved books, open the book menu, turn on My books, then choose a saved book."}
            {step === "sports" && "Choose the sports you follow."}
            {step === "done" &&
              "Your books are saved. Compare one +EV opportunity before deciding whether to bet."}
          </DialogDescription>
        </DialogHeader>

        {step !== "done" && (
          <div className="flex items-center justify-center gap-2 mt-1">
            <div
              className={cn(
                "h-1.5 w-8 rounded-full transition-colors",
                step === "books"
                  ? "bg-primary"
                  : "bg-primary/40",
              )}
            />
            <div
              className={cn(
                "h-1.5 w-8 rounded-full transition-colors",
                step === "sports"
                  ? "bg-primary"
                  : "bg-white/10",
              )}
            />
            <span className="text-xs text-muted-foreground ml-2">
              Step {step === "books" ? "1" : "2"} of 2
            </span>
          </div>
        )}

        {step === "books" && (
          <div className="mt-3 flex min-h-0 flex-col">
            <SportsbookSelectionField
              selectedBookIds={Array.from(selectedBooks) as SportsbookId[]}
              onChange={(bookIds) => {
                setSelectedBooks(new Set(bookIds));
                if (!bookIds.includes(defaultBookId) && bookIds[0]) {
                  setDefaultBookId(bookIds[0]);
                }
              }}
              disabled={sportsbookMeta.status !== "ready"}
            />

            {selectedBooks.size > 0 ? (
              <div className="mt-3 rounded-md border border-white/[0.07] bg-white/[0.02] p-3">
                <label className="mb-2 block text-xs font-semibold text-muted-foreground">
                  Default Book
                </label>
                <Select
                  value={defaultBookId}
                  onValueChange={(value) => setDefaultBookId(value as SportsbookId)}
                  disabled={sportsbookMeta.status !== "ready"}
                >
                  <SelectTrigger className="min-h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from(selectedBooks).map((bookId) => (
                      <SelectItem key={bookId} value={bookId}>
                        {sportsbookIdToDisplay(bookId)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className="mt-5 flex flex-col items-center gap-3">
              <Button
                onClick={handleBooksNext}
                className="gap-2 px-8"
                size="lg"
                disabled={selectedBooks.size === 0 || sportsbookMeta.status !== "ready"}
              >
                Next
                <ChevronRight className="size-4" />
              </Button>
              <button
                type="button"
                onClick={handleSkip}
                disabled={saving}
                className="text-sm text-muted-foreground/60 transition-colors hover:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                Skip, show me everything
              </button>
              {sportsbookMeta.status === "unavailable" ? (
                <div className="flex flex-col items-center gap-2" role="alert">
                  <p className="text-center text-xs text-destructive">
                    Book preferences are unavailable. Try again without leaving setup.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void sportsbookActions.retry()}
                  >
                    Retry
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {step === "sports" && (
          <div className="mt-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SPORTS_OPTIONS.map((sport) => {
                const isSelected = selectedSports.has(sport.id);
                return (
                  <button
                    key={sport.id}
                    type="button"
                    onClick={() => toggleSport(sport.id)}
                    aria-pressed={isSelected}
                    className={cn(
                      "flex items-center gap-2.5 rounded-md p-3 transition-colors",
                      "border hover:bg-white/5",
                      isSelected
                        ? "border-primary/60 bg-primary/10"
                        : "border-white/[0.06] bg-white/[0.02]",
                    )}
                  >
                    <span className="text-sm font-medium text-foreground/80">
                      {sport.label}
                    </span>
                    {isSelected && (
                      <Check className="size-3.5 text-primary ml-auto" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-5 flex flex-col items-center gap-3">
              <Button
                onClick={handleSportsComplete}
                className="gap-2 px-8"
                size="lg"
                disabled={saving}
              >
                {saving ? "Saving…" : "Finish Setup"}
                {!saving && <Check className="h-4 w-4" />}
              </Button>
              <button
                type="button"
                onClick={handleSkip}
                disabled={saving}
                className="text-sm text-muted-foreground/60 transition-colors hover:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                Skip, show me everything
              </button>
              {saveError ? (
                <p className="text-center text-xs text-destructive" role="alert">
                  {saveError}
                </p>
              ) : null}
            </div>
          </div>
        )}

        {step === "done" && (
          <div className="mt-4 space-y-2">
            <div className="flex items-start gap-3 rounded-lg bg-white/5 p-3">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary text-xs font-bold tabular-nums">
                90s
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  Understand One Edge
                </p>
                <p className="text-xs text-muted-foreground">
                  Compare the offered odds with the fair odds. +EV is an estimate,
                  not a guaranteed win.
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col items-center gap-4">
              <Button
                onClick={onStartTour}
                className="gap-2 px-8"
                size="lg"
              >
                <PlayCircle className="size-4" />
                Start Walkthrough
              </Button>
              <button
                type="button"
                onClick={onSkip}
                disabled={saving}
                className="text-sm text-muted-foreground/60 transition-colors hover:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                Open the Board
              </button>
            </div>

            <p className="mt-3 text-center text-[11px] text-muted-foreground/40">
              Restart anytime from the help menu
            </p>
            <p className="text-center text-xs text-muted-foreground">
              <Link
                href="/account"
                onClick={onSkip}
                className="text-primary underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                Review your plan and billing details in Account
              </Link>
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
